import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import ClockWidget from "@/components/ClockWidget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtTime } from "@/lib/format";
import { Check, X, Pencil, ShieldCheck, Loader2 } from "lucide-react";

const excBadge = {
  none: null,
  pending: <Badge className="bg-amber-500/20 text-amber-300">Pending</Badge>,
  approved: <Badge className="bg-emerald-500/20 text-emerald-300">Approved</Badge>,
  rejected: <Badge className="bg-red-500/20 text-red-300">Rejected</Badge>,
};

function Row({ a, isMgr, onEdit }) {
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="attendance-row">
      <td className="py-3 px-3">
        <div className="font-medium">{a.user_name}</div>
        <div className="text-xs text-muted-foreground">{a.date}</div>
      </td>
      <td className="py-3 px-3 font-mono text-sm">{fmtTime(a.clock_in)}</td>
      <td className="py-3 px-3 font-mono text-sm">{fmtTime(a.clock_out)}</td>
      <td className="py-3 px-3 text-sm text-muted-foreground">{a.expected_clock_out}</td>
      <td className="py-3 px-3">
        {excBadge[a.exception_status]}
        {a.edited && <span className="ml-2 text-[10px] text-muted-foreground">(edited)</span>}
      </td>
      {isMgr && (
        <td className="py-3 px-3 text-right">
          <Button size="sm" variant="ghost" onClick={() => onEdit(a)} data-testid="edit-attendance-btn"><Pencil className="w-4 h-4" /></Button>
        </td>
      )}
    </tr>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const isMgr = user?.role === "manager" || user?.role === "super_admin";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ clock_in: "", clock_out: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (isMgr) setAll((await api.get("/attendance")).data);
    else setMine((await api.get("/attendance/me")).data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const decide = async (a, decision) => {
    try {
      await api.post(`/attendance/${a.id}/decision`, { decision });
      toast.success(`Exception ${decision}.`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openEdit = (a) => {
    setEdit(a);
    setForm({ clock_in: a.clock_in?.slice(0, 16) || "", clock_out: a.clock_out?.slice(0, 16) || "", note: "" });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/attendance/${edit.id}`, {
        clock_in: form.clock_in || undefined,
        clock_out: form.clock_out || undefined,
        note: form.note,
      });
      toast.success("Record updated.");
      setEdit(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pending = all.filter((a) => a.exception_status === "pending");

  const Table = ({ rows }) => (
    <div className="card-emerald overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="attendance-history-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10">
              <th className="py-3 px-3">Employee / Date</th>
              <th className="py-3 px-3">Clock in</th>
              <th className="py-3 px-3">Clock out</th>
              <th className="py-3 px-3">Set time</th>
              <th className="py-3 px-3">Status</th>
              {isMgr && <th className="py-3 px-3"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={isMgr ? 6 : 5} className="py-10 text-center text-muted-foreground">No records yet.</td></tr>
            ) : rows.map((a) => <Row key={a.id} a={a} isMgr={isMgr} onEdit={openEdit} />)}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-emerald-400" />
        <h1 className="font-display text-2xl font-bold">Attendance</h1>
        <span className="text-xs text-muted-foreground ml-2">Immutable, audit-ready records</span>
      </div>

      {!isMgr ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 min-w-0"><ClockWidget onChange={load} /></div>
          <div className="lg:col-span-2 min-w-0"><Table rows={mine} /></div>
        </div>
      ) : (
        <Tabs defaultValue="all">
          <TabsList className="bg-[#121816] border border-emerald-500/15">
            <TabsTrigger value="all" data-testid="tab-all-records">All records</TabsTrigger>
            <TabsTrigger value="exceptions" data-testid="tab-exceptions">
              Exceptions {pending.length > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-amber-500/30 text-amber-200 text-[10px]">{pending.length}</span>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="mt-4"><Table rows={all} /></TabsContent>
          <TabsContent value="exceptions" className="mt-4 space-y-3">
            {pending.length === 0 ? (
              <div className="card-emerald p-10 text-center text-muted-foreground">No pending exceptions.</div>
            ) : pending.map((a) => {
              const early = a.early_out_request?.status === "pending";
              const reason = early ? a.early_out_request.reason : a.exception_reason;
              const outAt = a.clock_out || a.early_out_request?.requested_clock_out;
              return (
              <div key={a.id} className="card-emerald p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="exception-card">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{a.user_name} · {a.date}</p>
                    {early && <Badge className="bg-amber-500/20 text-amber-300">Early clock-out</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{early ? "Wants to leave at" : "Out at"} {fmtTime(outAt)} (set {a.expected_clock_out})</p>
                  <p className="text-sm mt-1 text-emerald-200/80">"{reason}"</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(a, "approved")} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217]" data-testid="approve-btn"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(a, "rejected")} className="border-red-500/40 text-red-300" data-testid="reject-btn"><X className="w-4 h-4 mr-1" /> Reject</Button>
                </div>
              </div>
              );
            })}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#121816] border-emerald-500/20">
          <DialogHeader><DialogTitle>Edit attendance — {edit?.user_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Clock in</Label>
              <Input type="datetime-local" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-clockin-input" />
            </div>
            <div>
              <Label>Clock out</Label>
              <Input type="datetime-local" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-clockout-input" />
            </div>
            <div>
              <Label>Reason for edit (audit)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Correcting missed punch" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="edit-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="save-edit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
